<template>
  <div class="bg-white dark:bg-[#0a0e19] py-[60px] md:py-[80px] lg:py-[135px]">
    <div
      class="mx-auto px-[12.5px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1255px]"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-[25px] items-center">
        <div
          class="xl:-mr-[25px] 2xl:-mr-[45px] rounded-[25px] order-2 lg:order-1"
        >
          <img
            src="@/assets/images/sign-in.jpg"
            alt="sign-in-image"
            class="rounded-[25px]"
          />
        </div>
        <div
          class="xl:pl-[90px] 2xl:pl-[120px] order-1 lg:order-2"
        >
          <img
            src="@/assets/images/logos/sidebarlogo.png"
            alt="logo"
            class="inline-block max-w-[160px]"
          />
          <div class="my-[17px] md:my-[25px]">
            <h1
              class="font-semibold text-[22px] md:text-xl lg:text-2xl mb-[5px] md:mb-[7px]"
            >
              Welcome Back!            </h1>
            <p class="font-medium lg:text-md text-[#445164] dark:text-gray-400">
              Sign in to your account
            </p>
          </div>
          
          <div v-if="errorMessage" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {{ errorMessage }}
          </div>
          
          <div class="mb-[15px] relative">
            <label
              class="mb-[10px] md:mb-[12px] text-black dark:text-white font-medium block"
            >
              Email Address
            </label>
            <input
              v-model="email"
              type="email"
              class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
              placeholder="example@clickai.live"
            />
          </div>
          <div class="mb-[15px] relative" id="passwordHideShow">
            <label
              class="mb-[10px] md:mb-[12px] text-black dark:text-white font-medium block"
            >
              Password
            </label>
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
              id="password"
              placeholder="Type password"
            />
            <button
              class="absolute text-lg right-[20px] bottom-[12px] transition-all hover:text-primary-500"
              @click="togglePassword"
              type="button"
            >
              <i :class="showPassword ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
            </button>
          </div>
          <RouterLink
            to="/authentication/forgot-password"
            class="inline-block text-primary-500 transition-all font-semibold hover:underline"
          >
            Forgot Password?
          </RouterLink>
          <button
            @click="handleSignIn"
            :disabled="isLoading"
            type="submit"
            class="md:text-md block w-full text-center transition-all rounded-md font-medium mt-[20px] md:mt-[25px] py-[12px] px-[25px] text-white bg-primary-500 hover:bg-primary-400 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span class="flex items-center justify-center gap-[5px]">
              <i v-if="!isLoading" class="material-symbols-outlined"> login </i>
              <span v-if="isLoading" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              {{ isLoading ? 'Signing In...' : 'Sign In' }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useRouter } from "vue-router";
import { authService, profileService } from "@/utils/supabase";

export default defineComponent({
  name: "SignIn",
  setup() {
    const router = useRouter();
    const email = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref("");

    const togglePassword = () => {
      showPassword.value = !showPassword.value;
    };

    const handleSignIn = async () => {
      if (!email.value || !password.value) {
        errorMessage.value = "Please enter both email and password";
        return;
      }

      isLoading.value = true;
      errorMessage.value = "";

      try {
        // נסה להתחבר עם Supabase
        const { data, error } = await authService.login(email.value, password.value);
        
        if (error) throw error;

        // בדוק האם יש פרופיל משתמש
        const { data: profileData, error: profileError } = await profileService.getCurrentProfile();
        
        if (profileError && profileError.message !== "User not authenticated") {
          // אם יש שגיאה אחרת מלבד "לא מאומת", נטפל בה
          console.error("Profile error:", profileError);
        }
        
        // אם אין פרופיל, ייתכן שזו הפעם הראשונה שהמשתמש מתחבר
        if (!profileData) {
          // ניתן להפנות למסך יצירת פרופיל או להמשיך לדף הבית
          console.log("No profile found. First time user?");
        }

        // עבור לדף הראשי לאחר התחברות מוצלחת
        router.push('/dashboard/crm');
      } catch (error: any) {
        console.error("Sign in error:", error);
        errorMessage.value = error.message || "Failed to sign in. Please try again.";
      } finally {
        isLoading.value = false;
      }
    };

    return {
      email,
      password,
      showPassword,
      isLoading,
      errorMessage,
      togglePassword,
      handleSignIn
    };
  }
});
</script>
